<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import dealStatusAPI from 'dashboard/api/pipeline/dealStatus';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import {
  formatLocalizedCurrency,
  normalizeCurrencyCode,
  normalizeLocale,
  parseLocalizedNumber,
} from '../utils/localeFormatting';
import {
  getCardOpportunitiesCount,
  getCardRealizedValue,
} from '../helpers/columnFilterMatchers';
import PipelineProductsAPI from 'dashboard/api/pipeline/products';
import { multiplicarDinheiro } from '../helpers/decimalMoney';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'success']);
const { t, locale } = useI18n();
const store = useStore();
const { showAlert } = useAlert();
const { isCloudFeatureEnabled } = useAccount();

// Fase 59 — Oportunidades é cross feature opt-in. Desligada, este modal volta a
// ser o de antes dela: valor livre, sem catálogo, sem razão de vendas. O
// fechamento em si nunca depende da feature.
const isOpportunitiesEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.PIPELINE_OPPORTUNITIES)
);

const submitting = ref(false);
const selectedOfferIndex = ref(null);
const wonValue = ref('');
const wonNote = ref('');
const valueTouched = ref(false);
const submitError = ref('');
const wonValueInputRef = ref(null);

const offers = computed(() => {
  const raw = props.item?.item_details?.offers;
  return Array.isArray(raw) ? raw : [];
});

const accountCurrency = computed(() => {
  const account = store.getters.getCurrentAccount || {};
  return account.currency || account.settings?.currency || 'BRL';
});

const currencyCode = rawCurrency => {
  const hasExplicitCurrency =
    rawCurrency !== undefined && rawCurrency !== null && rawCurrency !== '';
  const raw = hasExplicitCurrency
    ? rawCurrency
    : props.item.item_details?.currency || accountCurrency.value;
  return normalizeCurrencyCode(raw);
};

const selectedCurrencyCode = computed(() => {
  const selectedOffer = offers.value[selectedOfferIndex.value];
  return currencyCode(selectedOffer?.currency);
});

const itemTitle = computed(
  () =>
    props.item.item_details?.title ||
    props.item.title ||
    t('PIPELINE.UNTITLED_ITEM')
);

// Fase 59 — o card pode já ter venda no razão (foi ganho, reaberto e está sendo
// ganho de novo). Confirmar aqui grava uma venda NOVA, não corrige a anterior:
// o razão é append-only de propósito, e cards de recorrência vendem várias
// vezes. Sem dizer isso, quem reabriu para consertar um valor errado dobrava a
// receita sem perceber — o card mostra o último valor, o total soma os dois.
const recordedSalesCount = computed(() =>
  isOpportunitiesEnabled.value ? getCardOpportunitiesCount(props.item) : 0
);
const alreadyRecorded = computed(() => recordedSalesCount.value > 0);
const recordedTotalLabel = computed(() => {
  const total = getCardRealizedValue(props.item);
  if (total === null || total === undefined) return '';
  return (
    formatLocalizedCurrency(total, {
      currency: currencyCode(props.item.currency),
      locale: locale.value,
    }) || String(total)
  );
});

// ── Catálogo (Fase 59/F3) ────────────────────────────────────────────────────
//
// Fechar deixa de ser digitar um número solto e passa a ser escolher O QUE foi
// vendido, com o preço já cadastrado. É isso que liga a venda ao produto no
// razão — sem produto, ela não entra em nenhuma medição por produto.
//
// O catálogo é OPCIONAL: conta sem produtos (ou funil sem produto disponível)
// continua fechando por valor livre, exatamente como antes.
const catalog = ref([]);
const catalogLoading = ref(false);
const catalogFailed = ref(false);
const selectedProductId = ref(null);
const quantity = ref('1');
const unitValue = ref('');

// A moeda é do PRODUTO: formatar tudo na moeda da conta exibiria um item em USD
// como se fosse real — mesmo número, significado outro.
const formatMoney = (value, currency) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return (
    formatLocalizedCurrency(number, {
      currency: currencyCode(currency),
      locale: locale.value,
    }) || String(number)
  );
};

// A moeda do razão é congelada POR CARD — o modelo recusa duas moedas no mesmo
// card. Oferecer um produto em USD num card em BRL só levaria a uma recusa de
// validação (ou, pior, a um número certo com significado errado), então ele nem
// aparece como opção.
const cardCurrency = computed(() => currencyCode(props.item?.currency));
const selectableCatalog = computed(() =>
  catalog.value.filter(
    product => currencyCode(product.currency) === cardCurrency.value
  )
);
// Produtos existem, mas nenhum na moeda deste card: dizer isso é melhor que
// mostrar um catálogo vazio que parece "conta sem produtos".
const catalogHiddenByCurrency = computed(
  () => catalog.value.length > 0 && selectableCatalog.value.length === 0
);

const hasCatalog = computed(() => selectableCatalog.value.length > 0);
const selectedProduct = computed(
  () =>
    selectableCatalog.value.find(p => p.id === selectedProductId.value) || null
);

// Fase 59/F4 — com catálogo disponível, a venda é DE ALGUMA COISA. Um valor
// solto fica fora de qualquer medição por produto, e o razão é append-only: não
// há como vincular depois. Por isso, havendo o que escolher, escolher deixa de
// ser opcional.
//
// Sem catálogo — conta que ainda não cadastrou nada, funil sem produto na moeda
// do card, ou catálogo que falhou ao carregar — o valor livre continua sendo o
// caminho: travar o fechamento de um negócio já ganho seria pior que registrar
// a venda sem vínculo.
const missingProduct = computed(
  () => hasCatalog.value && !selectedProduct.value
);

// Teto do `per_page` do backend. O endpoint é paginado: uma página só deixaria
// o produto 201 impossível de escolher, e a venda dele seria registrada sem
// vínculo — exatamente o buraco que a F3 existe para fechar.
const CATALOG_PAGE_SIZE = 200;

// Teto para desistir do catálogo. O negócio JÁ foi ganho: um pedido pendurado
// não pode impedir de registrá-lo para sempre. Estourado o prazo, o modal cai
// no valor livre com o aviso de falha — pior que vincular, melhor que travar.
const CATALOG_TIMEOUT_MS = 10000;

let catalogRequestId = 0;

const loadCatalog = async () => {
  catalogRequestId += 1;
  const atual = catalogRequestId;
  const cardDoPedido = props.item?.id;
  // A CONTA também entra no guard: o modal é persistente e o mesmo id de card
  // existe em inquilinos diferentes — só o id deixaria a resposta de uma conta
  // pintar o catálogo de outra.
  const contaDoPedido = store.getters.getCurrentAccountId;
  const aindaVale = () =>
    atual === catalogRequestId &&
    cardDoPedido === props.item?.id &&
    contaDoPedido === store.getters.getCurrentAccountId;
  catalogLoading.value = true;
  catalogFailed.value = false;
  const prazo = setTimeout(() => {
    if (!aindaVale()) return;
    catalog.value = [];
    catalogFailed.value = true;
    catalogLoading.value = false;
    // Invalida o pedido: se ele responder depois, não pode ressuscitar a lista
    // debaixo de um operador que já está fechando por valor livre.
    catalogRequestId += 1;
  }, CATALOG_TIMEOUT_MS);
  try {
    const coletados = [];
    let offset = 0;
    let total = null;
    do {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await PipelineProductsAPI.list({
        activeOnly: true,
        pipelineId: props.item?.pipeline_id ?? undefined,
        perPage: CATALOG_PAGE_SIZE,
        offset,
      });
      // Revalida a CADA página: entre uma e outra a conta pode ter mudado, e
      // misturar páginas de inquilinos diferentes é pior que descartar tudo.
      if (!aindaVale()) return;
      const pagina = Array.isArray(data?.products) ? data.products : [];
      coletados.push(...pagina);
      total = Number(data?.meta?.total_count);
      offset += CATALOG_PAGE_SIZE;
      // Página vazia encerra: um total_count inconsistente viraria laço infinito.
      if (!pagina.length) break;
    } while (Number.isFinite(total) && coletados.length < total);

    if (!aindaVale()) return;
    catalog.value = coletados;
    catalogLoading.value = false;
  } catch {
    // Catálogo indisponível NÃO bloqueia o fechamento: o negócio foi ganho e
    // precisa ser registrado. Mas a tela DIZ que falhou — silenciar viraria
    // "esta conta não tem catálogo", e o operador registraria sem vínculo uma
    // venda que o razão nunca mais deixa corrigir.
    if (!aindaVale()) return;
    catalog.value = [];
    catalogFailed.value = true;
    catalogLoading.value = false;
  } finally {
    clearTimeout(prazo);
  }
};

const parseWonValue = raw => {
  const input = String(raw ?? '').trim();
  if (!input) return null;

  const localized = parseLocalizedNumber(input, locale.value, {
    currency: selectedCurrencyCode.value,
  });
  if (localized !== null) return localized;

  const canonical = Number(input);
  return Number.isFinite(canonical) ? canonical : null;
};

const hasValueError = computed(() => {
  const input = String(wonValue.value ?? '').trim();
  if (!input) return false;
  const parsed = parseWonValue(input);
  return parsed === null || parsed < 0;
});

// ── Quantidade × unitário (só com produto escolhido) ─────────────────────────
//
// O operador edita o UNITÁRIO, não o total: assim `quantidade × unitário` bate
// sempre com o total gravado. Deixar editar o total permitiria salvar 2 × 3.000
// somando 5.500 — três números que se contradizem na mesma linha do razão.
const parsedQuantity = computed(() => {
  const raw = String(quantity.value ?? '').trim();
  if (!raw) return null;
  const parsed = parseWonValue(raw);
  if (parsed === null || !Number.isFinite(parsed)) return null;
  return parsed > 0 ? parsed : null;
});

const parsedUnitValue = computed(() => {
  const raw = String(unitValue.value ?? '').trim();
  if (!raw) return null;
  const parsed = parseWonValue(raw);
  return parsed !== null && parsed >= 0 ? parsed : null;
});

const hasQuantityError = computed(
  () => Boolean(selectedProduct.value) && parsedQuantity.value === null
);
const hasUnitValueError = computed(
  () => Boolean(selectedProduct.value) && parsedUnitValue.value === null
);

// O total pode ser `null` mesmo com os dois campos válidos (notação científica,
// produto fora do inteiro seguro). Sem checar, `String(null)` iria como valor
// da venda para um razão que não desfaz.
// A moeda do card no rótulo: o modal do arrasto mostrava o símbolo e, sem ele,
// não se sabe em que moeda o valor livre está sendo digitado — num card em USD
// o operador digitaria pensando em real.
const freeValueLabel = computed(
  () => `${t('PIPELINE_MODALS.WON_VALUE_LABEL')} (${cardCurrency.value})`
);

const productTotal = computed(() => {
  if (!selectedProduct.value) return null;
  if (parsedQuantity.value === null || parsedUnitValue.value === null)
    return null;
  // Conta em inteiros, não em float: o servidor compara em decimal exato e
  // recusaria um total que o navegador arredondou de outro jeito — um 422 que o
  // operador não teria como corrigir, já que o número na tela é o rejeitado.
  return multiplicarDinheiro(parsedQuantity.value, parsedUnitValue.value);
});

// O total pode ser `null` mesmo com os dois campos válidos (notação científica,
// produto fora do inteiro seguro). Sem checar, `String(null)` iria como valor da
// venda para um razão que não desfaz.
const hasTotalError = computed(
  () => Boolean(selectedProduct.value) && productTotal.value === null
);

const productTotalLabel = computed(() => {
  if (productTotal.value === null) return '—';
  return (
    formatLocalizedCurrency(productTotal.value, {
      currency: currencyCode(selectedProduct.value?.currency),
      locale: locale.value,
    }) || String(productTotal.value)
  );
});

function selectProduct(id) {
  if (submitting.value) return;

  selectedProductId.value = id;
  const product = selectableCatalog.value.find(p => p.id === id);
  // Preço do catálogo como ponto de partida, não como camisa de força: o
  // desconto (ou o acréscimo) daquela negociação se digita por cima.
  unitValue.value =
    product?.default_value === null || product?.default_value === undefined
      ? ''
      : String(product.default_value);
  quantity.value = '1';
}

function clearProduct() {
  selectedProductId.value = null;
  unitValue.value = '';
  quantity.value = '1';
}

function selectOffer(index) {
  if (submitting.value) return;

  selectedOfferIndex.value = index;
  const offer = offers.value[index];
  if (offer?.value !== undefined && offer?.value !== null) {
    wonValue.value = String(offer.value);
    valueTouched.value = false;
  }
}

watch(
  // O modal é persistente: em PipelineColumn/CardDetails ele continua montado e
  // só troca de `item`. Observando apenas `show`, o catálogo do card anterior
  // ficava na tela E selecionável — e, entre contas, de outro inquilino.
  () => [props.show, props.item?.id, store.getters.getCurrentAccountId],
  ([visible]) => {
    submitting.value = false;
    valueTouched.value = false;
    submitError.value = '';
    selectedOfferIndex.value = null;
    wonValue.value = '';
    wonNote.value = '';
    catalog.value = [];
    clearProduct();

    // Sem a cross feature o catálogo nem é buscado: a rota responde 403, e
    // pedir para receber uma negativa só encheria o console de erro.
    if (visible && isOpportunitiesEnabled.value) loadCatalog();
    if (!visible) return;

    // Valor PREVISTO como ponto de partida — quem arrasta o card para Ganho
    // quase sempre fecha pelo valor que já estava na negociação. Digitar de
    // novo um número que o card já tem é trabalho que a tela pode poupar.
    const previsto =
      props.item?.expected_revenue ?? props.item?.item_details?.value;
    if (previsto !== null && previsto !== undefined && previsto !== '') {
      wonValue.value = String(previsto);
    }

    if (!offers.value.length) return;

    const defaultIndex = offers.value.findIndex(offer => offer?.is_default);
    let index = null;
    if (defaultIndex >= 0) index = defaultIndex;
    else if (offers.value.length === 1) index = 0;
    if (index !== null) selectOffer(index);
  },
  { immediate: true }
);

function formatOfferValue(offer) {
  if (offer?.value === undefined || offer?.value === null) return '';
  const currency = currencyCode(offer.currency);
  if (currency) {
    return (
      formatLocalizedCurrency(offer.value, {
        currency,
        locale: locale.value,
      }) || String(offer.value)
    );
  }

  const number = Number(offer.value);
  return Number.isFinite(number)
    ? new Intl.NumberFormat(normalizeLocale(locale.value)).format(number)
    : String(offer.value);
}

async function handleConfirm() {
  if (submitting.value) return;
  // Confirmar ANTES de o catálogo chegar registraria, de forma irreversível,
  // uma venda sem produto — com o produto certo aparecendo um segundo depois.
  if (catalogLoading.value) return;

  valueTouched.value = true;
  if (hasQuantityError.value || hasUnitValueError.value || hasTotalError.value)
    return;
  // Só valida o valor livre quando ele é o que está em uso: com produto
  // escolhido o campo está oculto, e tentar focá-lo travaria a confirmação sem
  // exibir erro nenhum.
  if (!selectedProduct.value && hasValueError.value) {
    await nextTick();
    wonValueInputRef.value?.$el?.querySelector('input')?.focus();
    return;
  }

  // Com produto escolhido, o valor fechado É quantidade × unitário — o mesmo
  // número que o operador está vendo antes de confirmar.
  // O card em que o fechamento COMEÇOU. Trocar de card (ou de conta) com o
  // pedido em voo faria a resposta ser aplicada ao card errado — e o PipelineTab
  // mesclaria uma venda alheia sobre ele.
  const cardDoEnvio = props.item?.id;
  const contaDoEnvio = store.getters.getCurrentAccountId;
  const rawValue = String(wonValue.value ?? '').trim();
  const parsedValue = selectedProduct.value
    ? productTotal.value
    : rawValue && parseWonValue(rawValue);
  submitting.value = true;
  submitError.value = '';
  try {
    const { data } = await dealStatusAPI.markAsWon(props.item.id, {
      wonValue:
        parsedValue === null || parsedValue === false || parsedValue === ''
          ? ''
          : String(parsedValue),
      wonNote: wonNote.value,
      winningOfferIndex: selectedOfferIndex.value,
      productId: selectedProductId.value ?? undefined,
      quantity: selectedProduct.value
        ? String(parsedQuantity.value)
        : undefined,
      unitValue: selectedProduct.value
        ? String(parsedUnitValue.value)
        : undefined,
    });

    if (
      cardDoEnvio !== props.item?.id ||
      contaDoEnvio !== store.getters.getCurrentAccountId
    ) {
      return;
    }

    // Compara o que foi PEDIDO com o que o servidor diz ter gravado. Num deploy
    // rolling, uma versão antiga aceita o produto, ignora e responde sucesso —
    // e a venda fica sem vínculo para sempre, porque o razão é append-only.
    // Compara o ID, não só "veio algo": outro produto também é o vínculo errado.
    if (
      selectedProductId.value &&
      data?.opportunity?.pipeline_product_id !== selectedProductId.value
    ) {
      showAlert(t('PIPELINE.DEAL_STATUS.ERROR.PRODUCT_NOT_LINKED'), 'error');
    } else {
      showAlert(t('PIPELINE.DEAL_STATUS.SUCCESS.MARKED_WON'));
    }
    emit('success', data);
    emit('close');
  } catch (error) {
    // Mesmo guard do sucesso: um erro do pedido ANTIGO não pode aparecer no
    // card novo — e nem apagar o `submitting` de um envio que ainda está em voo.
    if (
      cardDoEnvio !== props.item?.id ||
      contaDoEnvio !== store.getters.getCurrentAccountId
    ) {
      return;
    }

    // 409 é a recusa antifraude do servidor: o card já está fechado e o valor
    // de um negócio fechado não se altera em silêncio. Dizer isso — e o
    // caminho (reabrir) — vale mais que "falha ao atualizar status".
    submitError.value =
      error?.response?.status === 409
        ? t('PIPELINE.DEAL_STATUS.ERROR.ALREADY_WON')
        : t('PIPELINE.DEAL_STATUS.ERROR.MARK_FAILED');
    showAlert(submitError.value, 'error');
  } finally {
    if (
      cardDoEnvio === props.item?.id &&
      contaDoEnvio === store.getters.getCurrentAccountId
    ) {
      submitting.value = false;
    }
  }
}

function handleCancel() {
  if (!submitting.value) emit('close');
}
</script>

<template>
  <Modal
    :show="show"
    :close-on-backdrop-click="!submitting"
    :close-on-escape="!submitting"
    :show-close-button="!submitting"
    initial-focus-selector="input[name='winning-offer'], #mark-won-value-input"
    @close="handleCancel"
  >
    <section
      class="flex max-h-[min(90vh,48rem)] w-full flex-col overflow-hidden"
      :aria-busy="submitting"
      data-modal="mark-as-won"
    >
      <header class="px-4 pt-5 sm:px-6 sm:pt-6">
        <h2 class="mb-2 pr-10 text-heading-2 text-n-slate-12">
          {{ t('PIPELINE.DEAL_STATUS.WON_MODAL.TITLE') }}
        </h2>
        <p class="text-body-main text-n-slate-11">
          {{ t('PIPELINE.DEAL_STATUS.WON_MODAL.MESSAGE') }}
        </p>
      </header>

      <div class="overflow-y-auto px-4 py-5 sm:px-6">
        <div class="mb-5 rounded-lg border border-n-teal-9 bg-n-teal-3 p-4">
          <p class="break-words text-label font-medium text-n-teal-11">
            {{ itemTitle }}
          </p>
        </div>

        <!-- Card que JÁ vendeu: confirmar soma outra venda, não substitui a
             anterior. Quem reabriu para corrigir precisa saber disso ANTES de
             confirmar — depois, a receita já está dobrada. -->
        <p
          v-if="alreadyRecorded"
          class="mb-5 rounded-lg bg-n-amber-3 px-4 py-3 text-sm text-n-amber-11"
          role="status"
          data-testid="already-recorded"
        >
          {{
            t('PIPELINE.DEAL_STATUS.WON_MODAL.ALREADY_RECORDED', {
              count: recordedSalesCount,
              total: recordedTotalLabel,
            })
          }}
        </p>

        <fieldset v-if="offers.length" class="mb-5">
          <legend class="mb-2 text-label font-medium text-n-slate-12">
            {{ t('PIPELINE.DEAL_STATUS.WON_MODAL.SELECT_OFFER') }}
          </legend>
          <div class="max-h-48 space-y-2 overflow-y-auto">
            <label
              v-for="(offer, index) in offers"
              :key="index"
              class="flex min-h-12 cursor-pointer items-start gap-3 rounded-lg border border-n-weak p-3 transition-colors hover:bg-n-alpha-1"
              :class="{
                'border-n-teal-9 bg-n-teal-3': selectedOfferIndex === index,
              }"
            >
              <input
                :id="`winning-offer-${index}`"
                :checked="selectedOfferIndex === index"
                type="radio"
                name="winning-offer"
                :disabled="submitting"
                class="mt-0.5 size-5 shrink-0 accent-n-teal-9"
                @change="selectOffer(index)"
              />
              <span class="min-w-0 flex-1">
                <span class="block break-words text-label text-n-slate-12">
                  {{
                    offer.description ||
                    t('PIPELINE.DEAL_STATUS.WON_MODAL.OFFER_UNTITLED', {
                      index: index + 1,
                    })
                  }}
                </span>
                <span
                  class="mt-1 block text-label-small font-semibold text-n-teal-11"
                >
                  {{ formatOfferValue(offer) }}
                </span>
                <span
                  v-if="offer.is_default"
                  class="mt-1 inline-block rounded-full bg-n-amber-3 px-2 py-0.5 text-xs text-n-amber-11"
                >
                  {{ t('PIPELINE.DEAL_STATUS.WON_MODAL.DEFAULT_OFFER') }}
                </span>
              </span>
            </label>
          </div>
        </fieldset>

        <!-- Enquanto o catálogo carrega, o formulário de valor livre sozinho
             diria "esta conta não vende nada" — e o operador registraria sem
             vínculo uma venda que o razão não deixa mais corrigir. -->
        <p
          v-if="catalogLoading"
          class="mb-5 rounded-lg bg-n-alpha-1 px-4 py-3 text-sm text-n-slate-11"
          role="status"
        >
          {{ t('PIPELINE.DEAL_STATUS.WON_MODAL.CATALOG_LOADING') }}
        </p>

        <p
          v-else-if="catalogFailed"
          class="mb-5 rounded-lg bg-n-amber-3 px-4 py-3 text-sm text-n-amber-11"
          role="status"
        >
          {{ t('PIPELINE.DEAL_STATUS.WON_MODAL.CATALOG_FAILED') }}
        </p>

        <p
          v-else-if="catalogHiddenByCurrency"
          class="mb-5 rounded-lg bg-n-amber-3 px-4 py-3 text-sm text-n-amber-11"
          role="status"
        >
          {{
            t('PIPELINE.DEAL_STATUS.WON_MODAL.CATALOG_OTHER_CURRENCY', {
              currency: cardCurrency,
            })
          }}
        </p>

        <!-- Fase 59/F3 — O QUE foi vendido, com o preço já cadastrado. Só
             aparece quando há catálogo disponível NESTE funil; sem catálogo, o
             fechamento por valor livre continua igual ao de antes. -->
        <fieldset v-if="hasCatalog" class="mb-5">
          <legend class="mb-2 text-label font-medium text-n-slate-12">
            {{ t('PIPELINE.DEAL_STATUS.WON_MODAL.SELECT_PRODUCT') }}
          </legend>
          <div class="max-h-48 space-y-2 overflow-y-auto">
            <label
              v-for="product in selectableCatalog"
              :key="product.id"
              class="flex min-h-12 cursor-pointer items-center gap-3 rounded-lg border border-n-weak p-3 transition-colors hover:bg-n-alpha-1"
              :class="{
                'border-n-teal-9 bg-n-teal-3': selectedProductId === product.id,
              }"
            >
              <input
                :checked="selectedProductId === product.id"
                type="radio"
                name="won-product"
                :disabled="submitting"
                class="size-5 shrink-0 accent-n-teal-9"
                @change="selectProduct(product.id)"
              />
              <span class="min-w-0 flex-1">
                <span class="block break-words text-label text-n-slate-12">
                  {{ product.name }}
                </span>
                <span
                  v-if="product.category"
                  class="block text-label-small text-n-slate-10"
                >
                  {{ product.category }}
                </span>
              </span>
              <span
                class="shrink-0 text-label-small font-semibold tabular-nums text-n-teal-11"
              >
                {{
                  product.default_value === null ||
                  product.default_value === undefined ||
                  product.default_value === ''
                    ? t('PIPELINE.DEAL_STATUS.WON_MODAL.NO_PRICE')
                    : formatMoney(product.default_value, product.currency)
                }}
              </span>
            </label>
          </div>
          <!-- Havendo catálogo, escolher não é opcional: o botão de confirmar
               fica travado até aqui, e sem dizer por quê ele pareceria quebrado. -->
          <p
            v-if="missingProduct"
            class="mt-2 text-label-small text-n-ruby-11"
            role="status"
          >
            {{ t('PIPELINE.DEAL_STATUS.WON_MODAL.PRODUCT_REQUIRED') }}
          </p>
          <!-- A escapatória "fechar com valor livre" saiu daqui de propósito:
               havendo catálogo, a venda é sempre DE alguma coisa. Trocar de
               ideia é clicar em outro item da lista; o que não está no catálogo
               se cadastra em Pipeline → Oportunidades. -->
        </fieldset>

        <!-- Com produto: quantidade × unitário. O unitário nasce do catálogo e
             é EDITÁVEL — o desconto daquela negociação se digita por cima. -->
        <div v-if="selectedProduct" class="mb-4 flex flex-wrap gap-3">
          <div class="w-24 [&_input]:!h-12">
            <Input
              id="mark-won-quantity-input"
              v-model="quantity"
              type="text"
              name="won-quantity"
              inputmode="decimal"
              autocomplete="off"
              :disabled="submitting"
              :label="t('PIPELINE.DEAL_STATUS.WON_MODAL.QUANTITY_LABEL')"
              :message="
                valueTouched && hasQuantityError
                  ? t('PIPELINE.ERRORS.VALUE_INVALID')
                  : ''
              "
              :message-type="
                valueTouched && hasQuantityError ? 'error' : 'info'
              "
              :aria-invalid="valueTouched && hasQuantityError"
              @blur="valueTouched = true"
            />
          </div>
          <div class="min-w-32 flex-1 [&_input]:!h-12">
            <Input
              id="mark-won-unit-input"
              v-model="unitValue"
              type="text"
              name="won-unit-value"
              inputmode="decimal"
              autocomplete="off"
              :disabled="submitting"
              :label="t('PIPELINE.DEAL_STATUS.WON_MODAL.UNIT_VALUE_LABEL')"
              :message="
                valueTouched && hasUnitValueError
                  ? t('PIPELINE.ERRORS.VALUE_INVALID')
                  : ''
              "
              :message-type="
                valueTouched && hasUnitValueError ? 'error' : 'info'
              "
              :aria-invalid="valueTouched && hasUnitValueError"
              @blur="valueTouched = true"
            />
          </div>
          <div class="flex flex-col justify-center" data-testid="won-total">
            <span class="text-label-small uppercase text-n-slate-10">
              {{ t('PIPELINE.DEAL_STATUS.WON_MODAL.TOTAL_LABEL') }}
            </span>
            <span
              class="text-heading-2 font-bold tabular-nums text-n-teal-11"
              aria-live="polite"
            >
              {{ productTotalLabel }}
            </span>
          </div>
        </div>

        <div v-if="!selectedProduct" class="mb-4 [&_input]:!h-12">
          <Input
            id="mark-won-value-input"
            ref="wonValueInputRef"
            v-model="wonValue"
            type="text"
            name="won-value"
            inputmode="decimal"
            autocomplete="off"
            :disabled="submitting"
            :label="freeValueLabel"
            :placeholder="t('PIPELINE_MODALS.VALUE_PLACEHOLDER')"
            :message="
              valueTouched && hasValueError
                ? t('PIPELINE.ERRORS.VALUE_INVALID')
                : ''
            "
            :message-type="valueTouched && hasValueError ? 'error' : 'info'"
            :aria-invalid="valueTouched && hasValueError"
            @blur="valueTouched = true"
          />
        </div>

        <TextArea
          id="mark-won-note-textarea"
          v-model="wonNote"
          :label="t('PIPELINE.DEAL_STATUS.WON_MODAL.WON_NOTE_LABEL')"
          :placeholder="
            t('PIPELINE.DEAL_STATUS.WON_MODAL.WON_NOTE_PLACEHOLDER')
          "
          :disabled="submitting"
          auto-height
          min-height="4rem"
        />

        <p
          v-if="submitError"
          class="mt-4 rounded-lg bg-n-ruby-3 p-3 text-sm text-n-ruby-11"
          role="alert"
        >
          {{ submitError }}
        </p>
      </div>

      <footer
        class="flex flex-col-reverse gap-2 border-t border-n-weak px-4 py-4 sm:flex-row sm:px-6"
      >
        <Button
          variant="outline"
          color="slate"
          size="lg"
          :label="t('PIPELINE.DEAL_STATUS.WON_MODAL.CANCEL')"
          :disabled="submitting"
          class="w-full flex-1"
          @click="handleCancel"
        />
        <Button
          color="teal"
          size="lg"
          :label="t('PIPELINE.DEAL_STATUS.WON_MODAL.CONFIRM')"
          :is-loading="submitting || catalogLoading"
          :disabled="submitting || catalogLoading || missingProduct"
          class="w-full flex-1"
          @click="handleConfirm"
        />
      </footer>
    </section>
  </Modal>
</template>
